import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Skeleton,
  Card,
  CardBody,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Divider,
  Spinner,
  RadioGroup,
  Radio,
  Stack,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import {
  FiDollarSign,
  FiCheckCircle,
  FiAlertCircle,
  FiSend,
  FiCreditCard,
} from 'react-icons/fi';
import { 
  useGetSettlementSummary, 
  usePaySettlementByCategory,
  usePayAllSettlements,
  useGetCompanyWallet,
  useGetPaymentMethod,
  useGetMandateStatus,
  useUpdatePaymentMethod
} from '../../../services/payrollService';
import { showSuccessAlert, showErrorAlert } from '../../../utils/alert';
import { useAccessControl } from '../../../hooks/useAccessControl';

// Helper function to format currency with currency code
const formatCurrencyWithCode = (amount, currencyCode) => {
  if (amount === null || amount === undefined) return '0';
  
  const currencySymbols = {
    'USD': '$',
    'NGN': '₦',
    'GHS': '₵',
    'ZAR': 'R',
    'KES': 'KSh',
    'GBP': '£'
  };
  
  const symbol = currencySymbols[currencyCode] || '$';
  const numericAmount = parseFloat(amount) || 0;
  
  const formattedAmount = numericAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  return `${symbol}${formattedAmount}`;
};

const SettlementClearing = () => {
  const { canManageSettlements } = useAccessControl();
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const cardBg = useColorModeValue("white", "gray.700");
  
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const { isOpen: isPayAllOpen, onOpen: onPayAllOpen, onClose: onPayAllClose } = useDisclosure();
  const { isOpen: isPayCategoryOpen, onOpen: onPayCategoryOpen, onClose: onPayCategoryClose } = useDisclosure();
  const { isOpen: isPayAllConfirmOpen, onOpen: onPayAllConfirmOpen, onClose: onPayAllConfirmClose } = useDisclosure();
  const { isOpen: isPayCategoryConfirmOpen, onOpen: onPayCategoryConfirmOpen, onClose: onPayCategoryConfirmClose } = useDisclosure();
  const cancelRef = React.useRef();
  
  // API hooks
  const { 
    data: settlementSummaryData, 
    isLoading: settlementSummaryLoading,
    error: settlementSummaryError,
    refetch: refetchSettlementSummary
  } = useGetSettlementSummary();
  
  const { 
    data: walletData,
    isLoading: walletLoading
  } = useGetCompanyWallet();
  
  // Get payment method preference and mandate status
  const { data: paymentMethodData } = useGetPaymentMethod();
  const { data: mandateStatusData, refetch: refetchMandateStatus } = useGetMandateStatus();
  const { updatePaymentMethodPayload, updatePaymentMethodIsLoading } = useUpdatePaymentMethod();
  
  const payByCategoryMutation = usePaySettlementByCategory();
  const payAllMutation = usePayAllSettlements();
  
  // Check if mandate is active (same logic as AccountantApprovals)
  const mandate = mandateStatusData?.mandate;
  const mandateStatus = mandate?.status?.toLowerCase();
  const hasActiveMandate = 
    mandateStatus === "active" ||
    (mandateStatusData?.has_mandate === true && mandateStatus === "active") ||
    (mandate?.authorized_at && mandateStatus !== "pending" && mandateStatus !== "PENDING") ||
    mandateStatusData?.has_active_mandate === true ||
    paymentMethodData?.has_active_mandate === true ||
    false;
  
  // Pre-select payment method from preference if available
  React.useEffect(() => {
    if (paymentMethodData?.payment_method && !selectedPaymentMethod) {
      setSelectedPaymentMethod(paymentMethodData.payment_method);
    }
  }, [paymentMethodData, selectedPaymentMethod]);
  
  // Refetch mandate status when confirmation modal opens
  React.useEffect(() => {
    if (isPayCategoryConfirmOpen) {
      refetchMandateStatus();
    }
  }, [isPayCategoryConfirmOpen, refetchMandateStatus]);
  
  // Extract data
  const settlementSummary = settlementSummaryData?.data || settlementSummaryData;
  const wallet = walletData?.data || walletData;
  
  const summary = settlementSummary?.summary || {};
  const categories = settlementSummary?.categories || [];
  
  const walletBalance = wallet?.available_balance ?? wallet?.balance ?? wallet?.current_balance ?? 0;
  const pendingSettlement = wallet?.pending_settlement ?? 0;
  const walletCurrency = wallet?.currency || summary?.wallet_currency || 'NGN';
  
  const totalReadyToPay = summary?.total_ready_to_pay || 0;
  const readyCategories = categories.filter(cat => cat.ready_to_pay && cat.pending_amount > 0);
  
  const handlePayByCategory = (category) => {
    setSelectedCategory(category);
    onPayCategoryConfirmOpen();
  };
  
  const confirmPayByCategory = async () => {
    if (!selectedCategory || !selectedPaymentMethod) return;
    
    try {
      // If payment method is different from preference, update it first
      if (selectedPaymentMethod !== paymentMethodData?.payment_method) {
        try {
          await updatePaymentMethodPayload({ payment_method: selectedPaymentMethod });
        } catch (updateError) {
          console.error("Failed to update payment method preference:", updateError);
          // Continue with payment even if preference update fails
        }
      }
      
      // Make settlement payment with selected payment method
      const response = await payByCategoryMutation.mutateAsync({
        category_name: selectedCategory.category_name,
        category_type: selectedCategory.category_type,
        payment_method: selectedPaymentMethod
      });
      
      // Show success message based on payment method
      if (response?.payment_method === 'direct_debit') {
        showSuccessAlert(`Settlement payment for ${selectedCategory.category_name} initiated via Direct Debit. Payment is being processed.`);
      } else {
        const balanceAfter = response?.wallet_balance_after;
        const balanceMessage = balanceAfter !== null && balanceAfter !== undefined 
          ? ` New wallet balance: ${formatCurrencyWithCode(balanceAfter, walletCurrency)}`
          : '';
        showSuccessAlert(`Settlement payment for ${selectedCategory.category_name} processed successfully from wallet.${balanceMessage}`);
      }
      
      onPayCategoryConfirmClose();
      setSelectedCategory(null);
      setSelectedPaymentMethod(paymentMethodData?.payment_method || "wallet"); // Reset to preference
      refetchSettlementSummary();
    } catch (error) {
      console.error("Error paying settlement by category:", error);
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.error || 
                          "Failed to process settlement payment. Please try again.";
      showErrorAlert(errorMessage);
    }
  };
  
  const handlePayAll = () => {
    onPayAllConfirmOpen();
  };
  
  const confirmPayAll = async () => {
    try {
      // Use payment method preference for "Pay All" (or default to wallet)
      const paymentMethod = paymentMethodData?.payment_method || "wallet";
      
      const response = await payAllMutation.mutateAsync({
        payment_method: paymentMethod
      });
      
      // Show success message based on payment method
      if (response?.payment_method === 'direct_debit') {
        showSuccessAlert("All settlement payments initiated via Direct Debit. Payments are being processed.");
      } else {
        showSuccessAlert("All settlement payments processed successfully from wallet!");
      }
      
      onPayAllConfirmClose();
      refetchSettlementSummary();
    } catch (error) {
      console.error("Error paying all settlements:", error);
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.error || 
                          "Failed to process settlement payments. Please try again.";
      showErrorAlert(errorMessage);
    }
  };
  
  if (settlementSummaryLoading || walletLoading) {
    return (
      <VStack spacing={6} align="stretch">
        <Skeleton height="200px" borderRadius="lg" />
        <Skeleton height="400px" borderRadius="lg" />
      </VStack>
    );
  }
  
  if (settlementSummaryError) {
    return (
      <Alert status="error" borderRadius="lg">
        <AlertIcon />
        <AlertTitle>Failed to load settlement summary</AlertTitle>
        <AlertDescription>
          {settlementSummaryError?.message || "Unable to retrieve settlement information. Please try again."}
        </AlertDescription>
      </Alert>
    );
  }
  
  return (
    <VStack spacing={6} align="stretch">
      {/* Summary Cards */}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
        <Stat bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <StatLabel>Wallet Balance</StatLabel>
          <StatNumber color="blue.500">
            {formatCurrencyWithCode(walletBalance, walletCurrency)}
          </StatNumber>
          <StatHelpText>
            Available funds
          </StatHelpText>
        </Stat>

        <Stat bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <StatLabel>Ready to Pay</StatLabel>
          <StatNumber color="green.500">
            {formatCurrencyWithCode(totalReadyToPay, walletCurrency)}
          </StatNumber>
          <StatHelpText>
            {readyCategories.length} categories ready
          </StatHelpText>
        </Stat>

        <Stat bg={cardBg} p={4} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <StatLabel>Total Categories</StatLabel>
          <StatNumber color="purple.500">
            {categories.length}
          </StatNumber>
          <StatHelpText>
            With pending amounts
          </StatHelpText>
        </Stat>
      </SimpleGrid>
      
      {/* Quick Actions */}
      {totalReadyToPay > 0 && canManageSettlements() && (
        <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
          <CardBody>
            <HStack justify="space-between" align="center">
              <VStack align="start" spacing={1}>
                <Heading size="sm" color="gray.800">
                  Quick Actions
                </Heading>
                <Text fontSize="sm" color="gray.600">
                  Pay all ready settlements at once
                </Text>
              </VStack>
              <Button
                leftIcon={<FiSend />}
                colorScheme="green"
                size="lg"
                onClick={handlePayAll}
                isLoading={payAllMutation.isPending}
                isDisabled={totalReadyToPay === 0}
              >
                Pay All Settlements
              </Button>
            </HStack>
          </CardBody>
        </Card>
      )}
      
      {/* Categories Table */}
      {categories.length > 0 ? (
        <Card bg={cardBg} border="1px solid" borderColor={borderColor} overflow="hidden">
          <Box p={4} borderBottom="1px solid" borderColor={borderColor}>
            <Heading size="sm" color="gray.800">
              Settlement Categories
            </Heading>
            <Text fontSize="sm" color="gray.600" mt={1}>
              Pay settlements by category
            </Text>
          </Box>
          
          <TableContainer>
            <Table variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Category Name</Th>
                  <Th>Category Type</Th>
                  <Th>Pending Amount</Th>
                  <Th>Account Status</Th>
                  <Th>Payment Status</Th>
                  {canManageSettlements() && <Th>Actions</Th>}
                </Tr>
              </Thead>
              <Tbody>
                {categories.map((category, index) => (
                  <Tr key={index}>
                    <Td>
                      <Text fontWeight="600" color="gray.800">
                        {category.category_name}
                      </Text>
                    </Td>
                    <Td>
                      <Badge 
                        colorScheme={category.category_type === 'TAX' ? 'blue' : 'purple'}
                        variant="subtle"
                      >
                        {category.category_type}
                      </Badge>
                    </Td>
                    <Td>
                      <Text fontWeight="600" color="gray.800">
                        {formatCurrencyWithCode(category.pending_amount || 0, category.currency || walletCurrency)}
                      </Text>
                    </Td>
                    <Td>
                      {category.account_configured ? (
                        <Badge colorScheme="green" variant="subtle">
                          <HStack spacing={1}>
                            <FiCheckCircle />
                            <Text>Configured</Text>
                          </HStack>
                        </Badge>
                      ) : (
                        <Badge colorScheme="orange" variant="subtle">
                          <HStack spacing={1}>
                            <FiAlertCircle />
                            <Text>Not Configured</Text>
                          </HStack>
                        </Badge>
                      )}
                    </Td>
                    <Td>
                      {category.ready_to_pay ? (
                        <Badge colorScheme="green" variant="subtle">
                          Ready to Pay
                        </Badge>
                      ) : (
                        <Badge colorScheme="gray" variant="subtle">
                          Not Ready
                        </Badge>
                      )}
                    </Td>
                    {canManageSettlements() && (
                      <Td>
                        <Button
                          leftIcon={<FiCreditCard />}
                          colorScheme="blue"
                          size="sm"
                          onClick={() => handlePayByCategory(category)}
                          isDisabled={!category.ready_to_pay || category.pending_amount === 0}
                          isLoading={payByCategoryMutation.isPending && selectedCategory?.category_name === category.category_name}
                        >
                          Pay Now
                        </Button>
                      </Td>
                    )}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Card>
      ) : (
        <Alert status="info" borderRadius="lg">
          <AlertIcon />
          <Box>
            <AlertTitle>No Settlement Categories</AlertTitle>
            <AlertDescription>
              No pending settlement amounts found. Settlement amounts will appear here after payroll batches are completed.
            </AlertDescription>
          </Box>
        </Alert>
      )}
      
      {/* Insufficient Balance Alert */}
      {walletBalance < totalReadyToPay && totalReadyToPay > 0 && (
        <Alert status="warning" borderRadius="lg">
          <AlertIcon />
          <Box flex="1">
            <AlertTitle>Insufficient Available Balance</AlertTitle>
            <AlertDescription>
              Your available balance ({formatCurrencyWithCode(walletBalance, walletCurrency)}) is less than 
              the total ready to pay ({formatCurrencyWithCode(totalReadyToPay, walletCurrency)}).
              {pendingSettlement > 0 && ` Pending settlement: ${formatCurrencyWithCode(pendingSettlement, walletCurrency)} (not yet available).`}
              Please wait for settlement or fund your wallet before processing payments.
            </AlertDescription>
          </Box>
        </Alert>
      )}
      
      {/* Pay All Confirmation Dialog */}
      <AlertDialog
        isOpen={isPayAllConfirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={onPayAllConfirmClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Pay All Settlements
            </AlertDialogHeader>
            <AlertDialogBody>
              <VStack spacing={4} align="stretch">
                <Text>
                  You are about to process payments for all ready settlement categories.
                </Text>
                <Box p={3} bg="gray.50" borderRadius="md">
                  <Text fontSize="sm" fontWeight="600" mb={2}>
                    Payment Summary:
                  </Text>
                  <Text fontSize="sm">
                    Total Amount: <strong>{formatCurrencyWithCode(totalReadyToPay, walletCurrency)}</strong>
                  </Text>
                  <Text fontSize="sm">
                    Categories: <strong>{readyCategories.length}</strong>
                  </Text>
                  <Text fontSize="sm">
                    Wallet Balance: {formatCurrencyWithCode(walletBalance, walletCurrency)}
                  </Text>
                </Box>
                {walletBalance < totalReadyToPay && (
                  <Alert status="warning" size="sm">
                    <AlertIcon />
                    <Text fontSize="xs">
                      Insufficient available balance. Available: {formatCurrencyWithCode(walletBalance, walletCurrency)}, Required: {formatCurrencyWithCode(totalReadyToPay, walletCurrency)}.
                      {pendingSettlement > 0 && ` Pending settlement: ${formatCurrencyWithCode(pendingSettlement, walletCurrency)} (not yet available).`}
                    </Text>
                  </Alert>
                )}
                <Text fontSize="sm" color="gray.600">
                  This action cannot be undone. Continue?
                </Text>
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onPayAllConfirmClose}>
                Cancel
              </Button>
              <Button
                colorScheme="green"
                onClick={confirmPayAll}
                ml={3}
                isLoading={payAllMutation.isPending}
                isDisabled={walletBalance < totalReadyToPay}
              >
                Confirm Payment
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      
      {/* Pay Category Confirmation Dialog */}
      <AlertDialog
        isOpen={isPayCategoryConfirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={onPayCategoryConfirmClose}
        size="lg"
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Pay Settlement: {selectedCategory?.category_name}
            </AlertDialogHeader>
            <AlertDialogBody>
              <VStack spacing={4} align="stretch">
                <Text>
                  You are about to process payment for the selected category.
                </Text>
                {selectedCategory && (
                  <Box p={3} bg="gray.50" borderRadius="md">
                    <Text fontSize="sm" fontWeight="600" mb={2}>
                      Payment Details:
                    </Text>
                    <Text fontSize="sm">
                      Category: <strong>{selectedCategory.category_name}</strong>
                    </Text>
                    <Text fontSize="sm">
                      Type: <strong>{selectedCategory.category_type}</strong>
                    </Text>
                    <Text fontSize="sm">
                      Amount: <strong>{formatCurrencyWithCode(selectedCategory.pending_amount || 0, selectedCategory.currency || walletCurrency)}</strong>
                    </Text>
                    <Text fontSize="sm">
                      Wallet Balance: {formatCurrencyWithCode(walletBalance, walletCurrency)}
                    </Text>
                  </Box>
                )}
                
                {/* Wallet Balance Check for Wallet Payment */}
                {selectedCategory && selectedPaymentMethod === "wallet" && walletBalance < (selectedCategory.pending_amount || 0) && (
                  <Alert status="warning" size="sm">
                    <AlertIcon />
                    <Text fontSize="xs">
                      Insufficient available balance. Available: {formatCurrencyWithCode(walletBalance, walletCurrency)}, Required: {formatCurrencyWithCode(selectedCategory.pending_amount || 0, walletCurrency)}.
                      {pendingSettlement > 0 && ` Pending settlement: ${formatCurrencyWithCode(pendingSettlement, walletCurrency)} (not yet available).`}
                    </Text>
                  </Alert>
                )}
                
                {/* Payment Method Selection */}
                <Divider />
                <Box>
                  <FormControl isRequired isInvalid={!selectedPaymentMethod}>
                    <FormLabel fontSize="sm" fontWeight="600" color="gray.700" mb={3}>
                      Select Payment Method for This Transaction *
                    </FormLabel>
                    {!selectedPaymentMethod && (
                      <Alert status="warning" borderRadius="md" mb={3} fontSize="sm">
                        <AlertIcon />
                        <AlertDescription>
                          Please select a payment method to proceed
                        </AlertDescription>
                      </Alert>
                    )}
                    <RadioGroup
                      value={selectedPaymentMethod || ""}
                      onChange={setSelectedPaymentMethod}
                    >
                      <Stack spacing={3}>
                        {/* Wallet Option */}
                        <Box
                          p={3}
                          border="2px solid"
                          borderColor={selectedPaymentMethod === "wallet" ? "blue.500" : "gray.200"}
                          borderRadius="md"
                          bg={selectedPaymentMethod === "wallet" ? "blue.50" : "white"}
                          cursor="pointer"
                          _hover={{ borderColor: "blue.300" }}
                          onClick={() => setSelectedPaymentMethod("wallet")}
                        >
                          <HStack spacing={3}>
                            <Radio value="wallet" colorScheme="blue" />
                            <VStack align="start" spacing={1} flex={1}>
                              <HStack>
                                <FiDollarSign size={18} color={selectedPaymentMethod === "wallet" ? "#3182ce" : "#718096"} />
                                <Text fontWeight="600" fontSize="sm">
                                  Wallet Funding
                                </Text>
                                {paymentMethodData?.payment_method === "wallet" && (
                                  <Badge colorScheme="blue" variant="subtle" size="sm">
                                    Your Default
                                  </Badge>
                                )}
                              </HStack>
                              {walletData && (
                                <Text fontSize="xs" color="gray.600">
                                  Available: {formatCurrencyWithCode(
                                    walletData.available_balance ?? walletData.balance ?? walletData.current_balance ?? 0,
                                    walletCurrency
                                  )}
                                </Text>
                              )}
                            </VStack>
                          </HStack>
                        </Box>

                        {/* Direct Debit Option */}
                        <Box
                          p={3}
                          border="2px solid"
                          borderColor={
                            selectedPaymentMethod === "direct_debit"
                              ? hasActiveMandate
                                ? "blue.500"
                                : "orange.500"
                              : "gray.200"
                          }
                          borderRadius="md"
                          bg={
                            selectedPaymentMethod === "direct_debit"
                              ? hasActiveMandate
                                ? "blue.50"
                                : "orange.50"
                              : "white"
                          }
                          cursor={hasActiveMandate ? "pointer" : "not-allowed"}
                          opacity={hasActiveMandate ? 1 : 0.7}
                          _hover={hasActiveMandate ? { borderColor: "blue.300" } : {}}
                          onClick={() => {
                            if (hasActiveMandate) {
                              setSelectedPaymentMethod("direct_debit");
                            }
                          }}
                        >
                          <HStack spacing={3}>
                            <Radio
                              value="direct_debit"
                              colorScheme="blue"
                              isDisabled={!hasActiveMandate}
                            />
                            <VStack align="start" spacing={1} flex={1}>
                              <HStack>
                                <FiCreditCard
                                  size={18}
                                  color={
                                    selectedPaymentMethod === "direct_debit"
                                      ? hasActiveMandate
                                        ? "#3182ce"
                                        : "#dd6b20"
                                      : "#718096"
                                  }
                                />
                                <Text fontWeight="600" fontSize="sm">
                                  Direct Debit
                                </Text>
                                {paymentMethodData?.payment_method === "direct_debit" && hasActiveMandate && (
                                  <Badge colorScheme="blue" variant="subtle" size="sm">
                                    Your Default
                                  </Badge>
                                )}
                                {hasActiveMandate && mandate?.status === "active" && (
                                  <Badge colorScheme="green" variant="subtle" size="sm">
                                    Active
                                  </Badge>
                                )}
                                {!hasActiveMandate && (
                                  <Badge colorScheme="orange" variant="subtle" size="sm">
                                    Setup Required
                                  </Badge>
                                )}
                              </HStack>
                              {hasActiveMandate && mandate && (
                                <Text fontSize="xs" color="gray.600">
                                  Account: {mandate.account_number} ({mandate.bank_name})
                                </Text>
                              )}
                              {!hasActiveMandate && (
                                <Text fontSize="xs" color="orange.600">
                                  Please set up Direct Debit in Settings first
                                </Text>
                              )}
                            </VStack>
                          </HStack>
                        </Box>
                      </Stack>
                    </RadioGroup>
                  </FormControl>
                </Box>
                
                <Text fontSize="sm" color="gray.600">
                  This action cannot be undone. Continue?
                </Text>
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onPayCategoryConfirmClose}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={confirmPayByCategory}
                ml={3}
                isLoading={payByCategoryMutation.isPending || updatePaymentMethodIsLoading}
                isDisabled={
                  !selectedPaymentMethod ||
                  (selectedPaymentMethod === "wallet" && selectedCategory && walletBalance < (selectedCategory.pending_amount || 0)) ||
                  (selectedPaymentMethod === "direct_debit" && !hasActiveMandate)
                }
              >
                Confirm Payment
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </VStack>
  );
};

export default SettlementClearing;

